import * as Yup from "yup"
import {
	ssnRegex,
	einRegex,
	usCountryCodeRegex,
	intlCountryCodeRegex,
	usPhoneNumberRegex,
	intlPhoneNumberRegex,
	flexibleUrlRegex,
	yearOfIncorporation,
} from "../../../../utilities/validation"
import {CA_COUNTRY_CODE, caPostalCode, US_COUNTRY_CODE, usPostalCode} from "../../../../resources/address"
import {
	address,
	dateOfBirth,
	ein,
	fullName,
	matriculaConsular,
	nationality,
	passport,
	phone,
	ssn,
	website,
} from "../../../../utilities/validation"

// Individual validation sets
const IndividualAccountPurposeThatRequiresDetail: Set<string> = new Set([
	"InternationalRemittances",
	"CashHeavyPersonalIncome",
	"Cryptocurrency",
])

const IndividualTransactionVolumeThatRequiresDescription: Set<string> = new Set(["Between30KAnd60K", "GreaterThan60K"])

// Sole Prop validation sets
const SolePropAccountPurposeThatRequiresDetail: Set<string> = new Set([
	"CashHeavyIncomeAndOperations",
	"ProfessionalServicesHandlingFunds",
	"CharitableOrNonProfitOrganizationOperations",
	"CommercialCashOperations",
	"ThirdPartyPaymentProcessing",
])

const SolePropSourceOfFundsThatRequiresDescription: Set<string> = new Set([
	"GovernmentContractsOrGrants",
	"DonationsOrFundraising",
	"ImportExportRevenue",
])

const SolePropTransactionVolumeThatRequiresDescription: Set<string> = new Set(["Between150KAnd300K", "GreaterThan300K"])

const descriptionMinLengthError = "Must be at least 2 characters"

function descriptionValidation(name: string) {
	return Yup.string().test(name, descriptionMinLengthError, (value) => !value || value.replace(/\s/g, "").length >= 2)
}

const validationErrorMessages = {
	fullName: fullName,
	yearOfIncorporation: yearOfIncorporation,
	ssn: ssn,
	nationality: nationality,
	passport: passport,
	matriculaConsular: matriculaConsular,
	address: address,
	dateOfBirth: dateOfBirth,
	ein: ein,
	phone: phone,
	website: website,
}

export default {
	fullName: Yup.object({
		first: Yup.string().required(validationErrorMessages.fullName.first.required),
		last: Yup.string().required(validationErrorMessages.fullName.last.required),
	}),
	ssn: Yup.string().when("identificationType", {
		is: "ssn",
		then: Yup.string()
			.required(validationErrorMessages.ssn.required)
			.matches(ssnRegex, validationErrorMessages.ssn.invalid),
	}),
	nationality: Yup.string().when("identificationType", {
		is: "passport",
		then: Yup.string().required(validationErrorMessages.nationality.required),
	}),
	passport: Yup.string().when("identificationType", {
		is: "passport",
		then: Yup.string().required(validationErrorMessages.passport.required),
	}),
	matriculaConsular: Yup.string().when("identificationType", {
		is: "matriculaConsular",
		then: Yup.string().required(validationErrorMessages.matriculaConsular.required),
	}),
	address: Yup.object({
		street: Yup.string().required(validationErrorMessages.address.street.required),
		city: Yup.string().required(validationErrorMessages.address.city.required),
		state: Yup.string().when("country", {
			is: (country: string) => [US_COUNTRY_CODE, CA_COUNTRY_CODE].includes(country),
			then: Yup.string().required(validationErrorMessages.address.state.required),
		}),
		postalCode: Yup.string()
			.required(validationErrorMessages.address.postalCode.required)
			.when("country", {
				is: US_COUNTRY_CODE,
				then: Yup.string().matches(usPostalCode, validationErrorMessages.address.postalCode.usInvalid),
			})
			.when("country", {
				is: CA_COUNTRY_CODE,
				then: Yup.string().matches(caPostalCode, validationErrorMessages.address.postalCode.caInvalid),
			}),
		country: Yup.string().required(validationErrorMessages.address.country.required),
	}),

	dateOfBirth: Yup.date()
		.required(validationErrorMessages.dateOfBirth.required)
		.max(new Date(), validationErrorMessages.dateOfBirth.max),
	identificationType: Yup.string().oneOf(["ssn", "passport", "matriculaConsular"]),
	ein: Yup.string().matches(einRegex, validationErrorMessages.ein.invalid),
	phone: Yup.object({
		countryCode: Yup.string()
			.required(validationErrorMessages.phone.countryCode.required)
			.matches(
				new RegExp(`${usCountryCodeRegex.source}|${intlCountryCodeRegex.source}`),
				validationErrorMessages.phone.countryCode.required
			),
		number: Yup.string()
			.required(validationErrorMessages.phone.number.required)
			.when("countryCode", {
				is: (value: string) => usCountryCodeRegex.test(value),
				then: Yup.string().matches(usPhoneNumberRegex, validationErrorMessages.phone.number.usInvalid),
			})
			.when("countryCode", {
				is: (value: string) => intlCountryCodeRegex.test(value),
				then: Yup.string().matches(intlPhoneNumberRegex, validationErrorMessages.phone.number.intlInvalid),
			}),
	}),
	website: Yup.string().matches(flexibleUrlRegex, validationErrorMessages.website.invalid),
	yearOfIncorporation: Yup.string()
		.matches(/^\d{4}$/, validationErrorMessages.yearOfIncorporation.invalid)
		.when(["soleProprietorship", "isIncorporated"], {
			is: (soleProprietorship: boolean, isIncorporated: boolean) => soleProprietorship && isIncorporated,
			then: Yup.string()
				.matches(/^\d{4}$/, validationErrorMessages.yearOfIncorporation.invalid)
				.required("yearOfIncorporation is required."),
		}),
	stateOfIncorporation: Yup.string().when(["soleProprietorship", "isIncorporated"], {
		is: (soleProprietorship: boolean, isIncorporated: boolean) => soleProprietorship && isIncorporated,
		then: Yup.string().required("stateOfIncorporation is required."),
	}),
	sourceOfFundsDescription: Yup.string().when(["soleProprietorship", "sourceOfFunds"], {
		is: (soleProprietorship: boolean, sourceOfFunds: string) =>
			soleProprietorship && SolePropSourceOfFundsThatRequiresDescription.has(sourceOfFunds),
		then: descriptionValidation("sourceOfFundsDescription").required("sourceOfFundsDescription is required."),
		otherwise: descriptionValidation("sourceOfFundsDescription"),
	}),
	accountPurposeDetail: Yup.string().when(["soleProprietorship", "accountPurpose"], {
		is: (soleProprietorship: boolean, accountPurpose: string) =>
			soleProprietorship
				? SolePropAccountPurposeThatRequiresDetail.has(accountPurpose)
				: IndividualAccountPurposeThatRequiresDetail.has(accountPurpose),
		then: descriptionValidation("accountPurposeDetail").required("accountPurposeDetail is required."),
		otherwise: descriptionValidation("accountPurposeDetail"),
	}),
	transactionVolumeDescription: Yup.string().when(["soleProprietorship", "transactionVolume"], {
		is: (soleProprietorship: boolean, transactionVolume: string) =>
			soleProprietorship
				? SolePropTransactionVolumeThatRequiresDescription.has(transactionVolume)
				: IndividualTransactionVolumeThatRequiresDescription.has(transactionVolume),
		then: descriptionValidation("transactionVolumeDescription").required("transactionVolumeDescription is required."),
		otherwise: descriptionValidation("transactionVolumeDescription"),
	}),
	businessDescription: descriptionValidation("businessDescription"),
}
